import { COLORS } from '@/config/colors';
import { css, fillOutlined, circle, roundedPoly, shade } from './draw-utils';
import type { ProceduralDef } from '../types';

const STEEL = 0x4a4a58;
const HUSK = 0x6a5a80; // light swarm chassis

/** Turret — short-range rapid sentry guarding some arenas (M9). +X barrel. */
export const minionTurret: ProceduralDef = {
  key: 'minion/turret',
  width: 64,
  height: 64,
  anchorX: 0.5,
  anchorY: 0.5,
  frames: 2,
  draw(ctx, w, h, frame) {
    const c = w / 2;
    const hot = frame === 1;
    // Tripod feet
    ctx.strokeStyle = css(shade(STEEL, 0.6));
    ctx.lineWidth = 5;
    ctx.lineCap = 'round';
    for (const a of [2.4, Math.PI, -2.4]) {
      ctx.beginPath();
      ctx.moveTo(c + Math.cos(a) * 10, c + Math.sin(a) * 10);
      ctx.lineTo(c + Math.cos(a) * 24, c + Math.sin(a) * 24);
      ctx.stroke();
    }
    // Twin short barrels
    for (const dy of [-5, 5]) {
      roundedPoly(ctx, [[c + 8, c + dy - 2.5], [c + 28, c + dy - 2], [c + 28, c + dy + 2], [c + 8, c + dy + 2.5]], 2);
      fillOutlined(ctx, shade(STEEL, 0.85), 3);
    }
    // Swivel head
    circle(ctx, c, c, 13);
    fillOutlined(ctx, STEEL, 4.5);
    circle(ctx, c + 3, c, hot ? 6 : 4.5);
    fillOutlined(ctx, hot ? COLORS.dangerOrange : COLORS.dangerRed, 2.5);
    void h;
  },
};

/** Dome — heavy artillery shell charging ONE huge shot (M3). +X muzzle. */
export const minionDome: ProceduralDef = {
  key: 'minion/dome',
  width: 96,
  height: 96,
  anchorX: 0.5,
  anchorY: 0.5,
  frames: 2,
  draw(ctx, w, h, frame) {
    const c = w / 2;
    const charging = frame === 1;
    // Ground ring
    circle(ctx, c, c, 38);
    fillOutlined(ctx, shade(STEEL, 0.5), 5);
    // Dome shell with plate seams
    ctx.beginPath();
    ctx.arc(c, c, 30, 0, Math.PI * 2);
    fillOutlined(ctx, STEEL, 5.5);
    ctx.strokeStyle = css(shade(STEEL, 0.7));
    ctx.lineWidth = 3;
    for (const r of [12, 22]) {
      ctx.beginPath();
      ctx.arc(c, c, r, -1.1, 1.1);
      ctx.stroke();
    }
    // Big muzzle
    roundedPoly(ctx, [[c + 24, c - 8], [c + 44, c - 6], [c + 44, c + 6], [c + 24, c + 8]], 3);
    fillOutlined(ctx, shade(STEEL, 0.8), 4);
    // Charge maw
    circle(ctx, c + 38, c, charging ? 7 : 4);
    ctx.fillStyle = css(charging ? COLORS.lavaOrange : COLORS.dangerRed);
    ctx.fill();
    if (charging) {
      circle(ctx, c + 38, c, 13);
      ctx.fillStyle = 'rgba(255, 122, 48, 0.35)';
      ctx.fill();
    }
    void h;
  },
};

/** Dasher — paper-thin dart strafing in zigzags. Nose +X. */
export const minionDasher: ProceduralDef = {
  key: 'minion/dasher',
  width: 48,
  height: 48,
  anchorX: 0.5,
  anchorY: 0.5,
  frames: 1,
  draw(ctx, w, h) {
    const c = w / 2;
    roundedPoly(ctx, [[c + 18, c], [c - 8, c - 12], [c - 14, c], [c - 8, c + 12]], 4);
    fillOutlined(ctx, HUSK, 4.5);
    circle(ctx, c + 4, c, 4);
    fillOutlined(ctx, COLORS.dangerOrange, 2.5);
    void h;
  },
};

/** Weaver — winged orb stitching sine waves, drops mines. Nose +X. */
export const minionWeaver: ProceduralDef = {
  key: 'minion/weaver',
  width: 52,
  height: 52,
  anchorX: 0.5,
  anchorY: 0.5,
  frames: 2,
  draw(ctx, w, h, frame) {
    const c = w / 2;
    const flap = frame === 1 ? 0.65 : 1;
    for (const side of [-1, 1] as const) {
      roundedPoly(
        ctx,
        [[c - 2, c], [c - 14, c + side * 18 * flap], [c - 20, c + side * 8 * flap]],
        3,
      );
      fillOutlined(ctx, shade(HUSK, 1.2), 3.5);
    }
    circle(ctx, c, c, 11);
    fillOutlined(ctx, HUSK, 4);
    circle(ctx, c + 3, c - 2, 3.5);
    fillOutlined(ctx, COLORS.dangerRed, 2);
    void h;
  },
};

/** Splitter — twin-cored blob; bursts into mites on death. */
export const minionSplitter: ProceduralDef = {
  key: 'minion/splitter',
  width: 60,
  height: 60,
  anchorX: 0.5,
  anchorY: 0.5,
  frames: 1,
  draw(ctx, w, h) {
    const c = w / 2;
    // Lumpy twin-lobe body
    ctx.beginPath();
    ctx.arc(c - 7, c, 14, Math.PI * 0.4, Math.PI * 1.6);
    ctx.arc(c + 7, c, 14, Math.PI * 1.4, Math.PI * 0.6);
    ctx.closePath();
    fillOutlined(ctx, HUSK, 4.5);
    for (const dx of [-7, 7]) {
      circle(ctx, c + dx, c, 4.5);
      fillOutlined(ctx, COLORS.dangerOrange, 2);
    }
    // Split seam
    ctx.strokeStyle = css(shade(HUSK, 0.6));
    ctx.lineWidth = 2.5;
    ctx.setLineDash([3, 3]);
    ctx.beginPath();
    ctx.moveTo(c, c - 13);
    ctx.lineTo(c, c + 13);
    ctx.stroke();
    ctx.setLineDash([]);
    void h;
  },
};

/** Mite — one-hit speck that swarms on contact. */
export const minionMite: ProceduralDef = {
  key: 'minion/mite',
  width: 30,
  height: 30,
  anchorX: 0.5,
  anchorY: 0.5,
  frames: 1,
  draw(ctx, w, h) {
    const c = w / 2;
    circle(ctx, c, c, 9);
    fillOutlined(ctx, shade(HUSK, 0.85), 3.5);
    circle(ctx, c + 2, c, 3.5);
    ctx.fillStyle = css(COLORS.dangerRed);
    ctx.fill();
    void h;
  },
};

/** Sniper — long-barrel diamond that keeps its distance. Nose +X. */
export const minionSniper: ProceduralDef = {
  key: 'minion/sniper',
  width: 64,
  height: 64,
  anchorX: 0.5,
  anchorY: 0.5,
  frames: 2,
  draw(ctx, w, h, frame) {
    const c = w / 2;
    const aiming = frame === 1;
    roundedPoly(ctx, [[c + 6, c], [c - 4, c - 13], [c - 16, c], [c - 4, c + 13]], 4);
    fillOutlined(ctx, HUSK, 4.5);
    // Long rifle barrel
    roundedPoly(ctx, [[c + 4, c - 2], [c + 28, c - 1.5], [c + 28, c + 1.5], [c + 4, c + 2]], 1.5);
    fillOutlined(ctx, shade(STEEL, 0.9), 3);
    // Scope eye — glows while lining up the shot
    circle(ctx, c - 4, c, aiming ? 5.5 : 4);
    fillOutlined(ctx, aiming ? COLORS.lavaOrange : COLORS.dangerRed, 2.5);
    void h;
  },
};
